import { useFormContext } from "react-hook-form";
import { t } from "ttag";

import { FIELD_TYPE } from "@/features/forms/models";
import type { Form as DynamicFormType } from "@/features/forms/models";
import { ReadOnlyField } from "@/features/intake-form/components/healthie-form/components/healthie-form-field/components";
import { Form, Heading, Input, MarkdownContent, TextArea } from "@/shared/ui";
import { tw } from "@/shared/utils";

type Field = DynamicFormType["fields"][number];

const READ_ONLY_TYPES: string[] = [
  FIELD_TYPE.READ_ONLY,
  FIELD_TYPE.READ_ONLY_LINK,
];

const SINGLE_CHOICE_TYPES: string[] = [
  FIELD_TYPE.RADIO,
  FIELD_TYPE.HORIZONTAL_RADIO,
  FIELD_TYPE.DROPDOWN,
];

const NONE_OPTION_MATCHERS = [
  "none of the", // none of the above / below / these
  "none of these",
  "none of them",
  "ninguna de", // ninguna de las anteriores / siguientes / opciones
  "ninguno de", // ninguno de los anteriores / siguientes
  "ninguna de las anteriores",
  "ninguno de los anteriores",
];

export const isNoneOption = (label: string): boolean => {
  const normalized = label
    .toLowerCase()
    .replace(/[*_`~]/g, "")
    .trim();
  return NONE_OPTION_MATCHERS.some((matcher) => normalized.includes(matcher));
};

const optionCardStyles = (selected: boolean) =>
  tw(
    "flex min-h-11 cursor-pointer items-center justify-center rounded-xl border-2 px-3 py-2.5 text-center text-sm font-medium transition-colors",
    selected
      ? "border-secondary-500 bg-secondary-50 text-secondary-900"
      : "border-neutral-200 bg-white text-neutral-800 hover:border-secondary-300",
  );

const toArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === "string");
  }
  return typeof value === "string" && value.length > 0 ? [value] : [];
};

interface IntakeQuestionProps {
  field: Field;
  /**
   * Called after a value changes. `autoAdvance` signals that the interaction is
   * a terminating single choice (radio) or a "none of the above/below" answer,
   * so the parent may move to the next step if nothing else was revealed.
   */
  onAnswer?: (fieldId: string, autoAdvance: boolean) => void;
}

export const IntakeQuestion = ({ field, onAnswer }: IntakeQuestionProps) => {
  const { control } = useFormContext();

  if (READ_ONLY_TYPES.includes(field.type)) {
    return <ReadOnlyField healthieField={field} />;
  }

  const isSingleChoice = SINGLE_CHOICE_TYPES.includes(field.type);
  const isCheckbox = field.type === FIELD_TYPE.CHECKBOX;
  const isTextArea = field.type === FIELD_TYPE.TEXTAREA;

  return (
    <Form.Field
      control={control}
      name={field.id}
      defaultValue={field.value ?? ""}
      rules={{
        validate: (value) => {
          if (!field.required) {
            return true;
          }
          const hasValue = Array.isArray(value)
            ? value.length > 0
            : value !== undefined &&
              value !== null &&
              String(value).trim() !== "";
          return hasValue || t`This field is required`;
        },
      }}
      render={({ field: rhfField }) => (
        <Form.Item className="mf-masked mf-listen flex flex-col gap-4">
          {field.label && (
            <Heading level={3} size="medium">
              {field.label}
            </Heading>
          )}

          <Form.Control>
            {isSingleChoice ? (
              <div className="grid gap-3 sm:grid-cols-2">
                {field.options?.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={optionCardStyles(rhfField.value === option)}
                    onClick={() => {
                      rhfField.onChange(option);
                      onAnswer?.(field.id, true);
                    }}
                  >
                    <MarkdownContent>{option}</MarkdownContent>
                  </button>
                ))}
              </div>
            ) : isCheckbox ? (
              <div className="grid gap-3 sm:grid-cols-2">
                {field.options?.map((option) => {
                  const selectedValues = toArray(rhfField.value);
                  const checked = selectedValues.includes(option);
                  const noneOption = isNoneOption(option);

                  return (
                    <button
                      key={option}
                      type="button"
                      className={optionCardStyles(checked)}
                      onClick={() => {
                        if (noneOption) {
                          rhfField.onChange([option]);
                          onAnswer?.(field.id, true);
                          return;
                        }

                        const withoutNone = selectedValues.filter(
                          (value) => !isNoneOption(value),
                        );
                        const next = checked
                          ? withoutNone.filter((value) => value !== option)
                          : [...withoutNone, option];

                        rhfField.onChange(next);
                        onAnswer?.(field.id, false);
                      }}
                    >
                      <MarkdownContent>{option}</MarkdownContent>
                    </button>
                  );
                })}
              </div>
            ) : isTextArea ? (
              <TextArea placeholder={t`Type your answer here`} {...rhfField} />
            ) : (
              <Input.Root
                type={field.type === FIELD_TYPE.NUMBER ? "number" : "text"}
                min={field.type === FIELD_TYPE.NUMBER ? 0 : undefined}
                placeholder={t`Type your answer here`}
                {...rhfField}
              />
            )}
          </Form.Control>

          <Form.Message compact={false} />
        </Form.Item>
      )}
    />
  );
};
